import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api'; // (Existe como export default)
import { useCart } from '../context/CartContext'; // (Existe)
import { Product, ProductVariant, LocationInventory } from '../types'; // (Tipo 'Product')
import { formatCurrency } from '../utils';

// Helper para obtener stock por ubicación de una variante
const getVariantStockByLocation = (variant: ProductVariant | null, location: 'SANTIAGO' | 'ANTOFAGASTA'): number => {
  if (!variant?.location_inventory || !Array.isArray(variant.location_inventory)) {
    return 0;
  }
  const inv = variant.location_inventory.find((inv: LocationInventory) => inv.location === location);
  return inv?.quantity || 0;
};

export const ProductDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [showLocationError, setShowLocationError] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);

  const { addItemToCart } = useCart();

  useEffect(() => {
    const fetchProduct = async () => {
      if (!slug) return;

      setLoading(true);
      setError(null);
      try {
        // Soporta slug (Django) o id numérico (backend Python)
        const isNumericId = /^\d+$/.test(slug);
        const url = isNumericId
          ? `/products/${slug}/`
          : `/products/products/${slug}/`;
        const response = await api.get<Product>(url);
        const data = response.data as any;
        setProduct(data);
        
        // Lógica de inicialización de imagen y variante
        let initialImage = data.image || data.images?.[0]?.image || '';

        // Si el producto usa variantes, seleccionar la primera activa por defecto
        if (data.use_variants && data.variants && data.variants.length > 0) {
            const firstActive = data.variants.find((v: ProductVariant) => v.is_active);
            if (firstActive) {
                setSelectedVariant(firstActive);
                if (firstActive.image) {
                    initialImage = firstActive.image;
                }
            }
        }

        setSelectedImage(initialImage);
      } catch (err) {
        setError('No se pudo cargar el producto.');
        console.error('Error al cargar producto:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [slug]); // Se ejecuta cada vez que el 'slug' en la URL cambia

  const handleVariantSelect = (variant: ProductVariant) => {
    setSelectedVariant(variant);
    if (variant.image) {
        setSelectedImage(variant.image);
    }
  };

  const handleAddToCart = async () => {
    if (product) {
      // Validar que se haya seleccionado una ubicación
      if (!selectedLocation) {
        setShowLocationError(true);
        return;
      }
      
      try {
        await addItemToCart(String(product.id), 1, selectedLocation, selectedVariant?.id);
        setShowLocationError(false);
        // Trigger cart icon shake animation
        const cartIcon = document.getElementById('cart-icon');
        if (cartIcon) {
          cartIcon.classList.add('cart-shake');
          setTimeout(() => {
            cartIcon.classList.remove('cart-shake');
          }, 700);
        }
      } catch (err) {
        console.error('Error al agregar al carrito:', err);
        alert('Error al agregar el producto al carrito. Por favor, intenta nuevamente.');
      }
    }
  };

  // Combinar imagen principal (si no está en la lista) con la galería e incluir imágenes de variantes
  const images = React.useMemo(() => {
    if (!product) return [];
    let imgs = [...(product.images || [])];

    // Agregar imágenes de variantes si no están ya en la lista
    if (product.variants) {
      product.variants.forEach((v) => {
        if (v.image && !imgs.some((img) => img.image === v.image)) {
          // Usamos un ID negativo temporal para evitar colisiones con IDs de base de datos
          imgs.push({ 
            id: -v.id, 
            image: v.image, 
            is_main: false 
          } as any);
        }
      });
    }

    // Si no hay imágenes en la galería pero sí hay imagen principal, usar esa
    if (imgs.length === 0 && product.image) {
      return [{ id: 0, image: product.image, is_main: true }];
    }
    return imgs;
  }, [product]);

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!images.length) return;
    const currentIndex = images.findIndex((img) => img.image === selectedImage);
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
    setSelectedImage(images[prevIndex].image);
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!images.length) return;
    const currentIndex = images.findIndex((img) => img.image === selectedImage);
    const nextIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
    setSelectedImage(images[nextIndex].image);
  };

  if (loading) {
    return <div className="text-center p-12">Cargando producto...</div>;
  }

  if (error) {
    return <div className="text-center p-12 text-red-500">{error}</div>;
  }

  if (!product) {
    return <div className="text-center p-12">Producto no encontrado.</div>;
  }

  // Renderizado del Producto (Diseño minimalista inspirado en Adidas, con paleta Frahda)
  return (
    <div className="max-w-7xl mx-auto px-6 md:px-8 py-8 pt-[96px]">
      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="mb-6 font-sans text-sm text-gray-600">
        <Link to="/" className="hover:text-primary-accent">Inicio</Link>
        <span className="mx-2">/</span>
        <Link to="/products" className="hover:text-primary-accent">Tienda</Link>
        <span className="mx-2">/</span>
        <span>{product.categories?.[0]?.name ?? 'Categoría'}</span>
      </nav>

      {/* Grid principal: 2 columnas en desktop */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
        {/* Columna Izquierda: Galería de Imágenes */}
        <div className="md:col-span-3">
          <div className="w-full border border-gray-200 relative group bg-gray-50 flex items-center justify-center overflow-hidden" style={{ minHeight: '400px', maxHeight: '600px' }}>
            <img
              src={selectedImage || product.image || images[0]?.image}
              alt={product.name}
              className="w-full h-full object-contain max-h-[600px]"
            />
            {/* Flechas de navegación (Ruleta) */}
            {images.length > 1 && (
              <>
                <button 
                  onClick={handlePrevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 p-3 rounded-full shadow-md hover:bg-white opacity-0 group-hover:opacity-100 transition-all duration-300 focus:opacity-100 z-10"
                  aria-label="Imagen anterior"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button 
                  onClick={handleNextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 p-3 rounded-full shadow-md hover:bg-white opacity-0 group-hover:opacity-100 transition-all duration-300 focus:opacity-100 z-10"
                  aria-label="Siguiente imagen"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}
          </div>

          {/* Miniaturas opcionales */}
          {images.length > 0 && (
            <div className="grid grid-cols-5 gap-3 mt-4">
              {images.map((img, idx) => {
                const isActive = selectedImage === img.image;
                return (
                  <button
                    key={img.id || idx}
                    type="button"
                    aria-label={`Seleccionar imagen ${idx + 1}`}
                    onClick={() => setSelectedImage(img.image)}
                    className={`border-2 rounded-md overflow-hidden aspect-square ${isActive ? 'border-gray-900' : 'border-transparent hover:border-gray-300'} transition-all duration-200`}
                  >
                    <img
                      src={img.image}
                      alt={`${product.name} ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Columna Derecha: Información y CTA (sticky) */}
        <div className="md:col-span-2">
          <div className="sticky top-5 space-y-4">
            <h1 className="font-serif text-4xl font-bold text-gray-900">
              {product.name}
            </h1>
            
            {/* Precio con descuento */}
            <div className="space-y-2">
              {product.discount_percentage && product.discount_percentage > 0 ? (
                <>
                  <div className="flex items-center space-x-3">
                    <p className="font-sans text-3xl font-bold text-red-600">
                      {formatCurrency(Number(product.discount_price || product.price))}
                    </p>
                    <span className="inline-flex items-center px-3 py-1 rounded text-sm font-bold bg-black text-white">
                      -{product.discount_percentage}% OFF
                    </span>
                  </div>
                  <p className="font-sans text-xl text-gray-500 line-through">
                    {formatCurrency(Number(product.price))}
                  </p>
                </>
              ) : (
                <p className="font-sans text-3xl font-semibold text-gray-900">
                  {formatCurrency(Number(product.price))}
                </p>
              )}
            </div>
            
            {/* Selector de Variantes (Tonos) */}
            {product.use_variants && product.variants && product.variants.length > 0 && (
                <div className="space-y-3 border-t pt-4">
                    <label className="font-sans text-sm font-medium text-gray-900 block">
                        Selecciona tono
                    </label>
                    <div className="flex flex-wrap gap-3">
                        {product.variants.filter(v => v.is_active).map((variant) => (
                            <button
                                key={variant.id}
                                onClick={() => handleVariantSelect(variant)}
                                className={`w-8 h-8 rounded-full border transition-all relative group ${
                                    selectedVariant?.id === variant.id 
                                        ? 'ring-2 ring-offset-2 ring-gray-900 scale-110' 
                                        : 'border-gray-300 hover:scale-110'
                                }`}
                                style={{ backgroundColor: variant.color_hex || '#e5e7eb' }}
                                title={variant.name}
                                aria-label={`Seleccionar tono ${variant.name}`}
                            >
                                {/* Tooltip simple */}
                                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-black rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                  {variant.name}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
            
            {/* Selector de Ubicación */}
            <div className="space-y-3 border-t pt-4">
              <label className="font-sans text-sm font-medium text-gray-900 block">
                Selecciona stock:
              </label>
              
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedLocation('SANTIAGO');
                    setShowLocationError(false);
                  }}
                  className={`border-2 rounded-md py-3 px-4 text-sm font-medium transition-all ${
                    selectedLocation === 'SANTIAGO'
                      ? 'border-gray-900 bg-gray-900 text-white'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-center">
                    <div className="font-bold">Santiago</div>
                    <div className="text-xs mt-1 opacity-80">
                      {selectedVariant 
                        ? getVariantStockByLocation(selectedVariant, 'SANTIAGO')
                        : ((product as any).stock_locations?.find((l: any) => l.location === 'SANTIAGO')?.quantity || 0)
                      } disponibles
                    </div>
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => {
                    setSelectedLocation('ANTOFAGASTA');
                    setShowLocationError(false);
                  }}
                  className={`border-2 rounded-md py-3 px-4 text-sm font-medium transition-all ${
                    selectedLocation === 'ANTOFAGASTA'
                      ? 'border-gray-900 bg-gray-900 text-white'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-center">
                    <div className="font-bold">Antofagasta</div>
                    <div className="text-xs mt-1 opacity-80">
                      {selectedVariant 
                        ? getVariantStockByLocation(selectedVariant, 'ANTOFAGASTA')
                        : ((product as any).stock_locations?.find((l: any) => l.location === 'ANTOFAGASTA')?.quantity || 0)
                      } disponibles
                    </div>
                  </div>
                </button>
              </div>
              
              {showLocationError && (
                <p className="text-sm text-red-600 mt-2">
                  ⚠️ Por favor, selecciona una ubicación antes de agregar al carrito
                </p>
              )}
            </div>
            
            <p className="font-sans text-sm text-gray-500">
              {selectedVariant 
                ? selectedVariant.total_stock
                : ((product as any).total_stock || product.stock)
              } disponibles en total
            </p>

            <button
              onClick={handleAddToCart}
              disabled={product.stock <= 0}
              className={`w-full bg-primary-accent text-white rounded-none py-3 font-bold transition-colors ${
                product.stock > 0 ? 'hover:bg-primary-accent/90' : 'opacity-60 cursor-not-allowed'
              }`}
            >
              Agregar al Carrito
            </button>
          </div>
        </div>
      </div>

      {/* Sección inferior: Acordeón */}
      <section className="mt-12">
        <div className="border-t border-gray-200 pt-6">
          {/* Acordeón simple basado en FAQ */}
          <Accordion>
            <AccordionItem title="Descripción">
              <p className="text-sm md:text-base leading-relaxed text-gray-700 whitespace-pre-wrap">
                {product.description || 'Este producto no tiene una descripción detallada.'}
              </p>
            </AccordionItem>
            
            {product.ingredients && (
              <AccordionItem title="Ingredientes">
                <p className="text-sm md:text-base leading-relaxed text-gray-700 whitespace-pre-wrap">
                  {product.ingredients}
                </p>
              </AccordionItem>
            )}

            {product.usage_instructions && (
              <AccordionItem title="Modo de Uso">
                <p className="text-sm md:text-base leading-relaxed text-gray-700 whitespace-pre-wrap">
                  {product.usage_instructions}
                </p>
              </AccordionItem>
            )}

            {product.benefits && (
              <AccordionItem title="Beneficios">
                <p className="text-sm md:text-base leading-relaxed text-gray-700 whitespace-pre-wrap">
                  {product.benefits}
                </p>
              </AccordionItem>
            )}

            <AccordionItem title="Detalles del Producto">
              <ul className="text-sm md:text-base text-gray-700 space-y-1">
                <li>Nombre: {product.name}</li>
                <li>Categoría: {product.categories?.map(c => c.name).join(', ') ?? 'N/A'}</li>
                {product.discount_percentage && product.discount_percentage > 0 ? (
                  <>
                    <li>Precio: <span className="text-red-600 font-semibold">{formatCurrency(Number(product.discount_price || product.price))}</span> <span className="text-gray-500 line-through text-sm">{formatCurrency(Number(product.price))}</span> <span className="text-xs bg-black text-white px-2 py-0.5 rounded">-{product.discount_percentage}%</span></li>
                  </>
                ) : (
                  <li>Precio: {formatCurrency(Number(product.price))}</li>
                )}
                <li>Stock: {product.stock}</li>
                {product.size && <li>Tamaño: {product.size}</li>}
                {product.hair_type && <li>Tipo de Cabello: {product.hair_type}</li>}
              </ul>
            </AccordionItem>
            <AccordionItem title="Valoraciones">
              <p className="text-sm md:text-base text-gray-500">Aún no hay valoraciones para este producto.</p>
            </AccordionItem>
          </Accordion>
        </div>
      </section>
    </div>
  );
};

// Acordeón mínimo reutilizable (similar al de FAQ)
const Accordion: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="max-w-3xl">
      <div className="divide-y">
        {children}
      </div>
    </div>
  );
};

const AccordionItem: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  const [open, setOpen] = useState(false);
  const [height, setHeight] = useState<number>(0);
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (contentRef.current) {
      setHeight(open ? contentRef.current.scrollHeight : 0);
    }
  }, [open]);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center justify-between py-4 text-left transition-colors hover:text-primary-accent"
      >
        <span className="font-medium">{title}</span>
        <span className={`text-xl leading-none select-none transition-transform duration-300 ${open ? 'rotate-180' : ''}`}>
          {open ? '-' : '+'}
        </span>
      </button>
      <div
        ref={contentRef}
        style={{ height: `${height}px` }}
        className="overflow-hidden transition-all duration-500 ease-in-out"
      >
        <div className="pb-4">{children}</div>
      </div>
    </div>
  );
};